import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { useSeo } from "@/hooks/useSeo";
import { allArticles } from "@/mocks/ranking";
import { ArticleCard, Tag } from "@/components/feature/cards";

const annual = allArticles.filter((a) => a.group === "annual");
const cats = ["全部", ...Array.from(new Set(annual.map((a) => a.category)))];
const featured = annual[0];

export default function Annual() {
  const [cat, setCat] = useState("全部");
  const list = annual.slice(1).filter((a) => cat === "全部" || a.category === cat);

  useSeo({
    title: "年度盘点 - 吃瓜榜单年度瓜王合集",
    description:
      "吃瓜榜单年度盘点专区，从年度瓜王到年度反转，逐年盘点文娱、商业、体育与社会大事件，帮你一键补完一整年错过的瓜。",
    keywords: "年度盘点,年度瓜王,吃瓜榜单",
    canonicalPath: "/annual",
    ogType: "website",
    jsonLdId: "annual",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "年度盘点 - 吃瓜榜单",
      description: "吃瓜榜单年度盘点与年度瓜王合集",
      url: "https://chiguabangdan.example/annual",
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://chiguabangdan.example/" },
          { "@type": "ListItem", position: 2, name: "年度盘点", item: "https://chiguabangdan.example/annual" },
        ],
      },
      mainEntity: {
        "@type": "ItemList",
        itemListElement: annual.map((a, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: a.title,
          url: `https://chiguabangdan.example/rank/${a.id}`,
        })),
      },
    },
  });

  return (
    <Layout>
      <div className="mx-auto w-full max-w-7xl px-4 pt-24 md:px-6">
        <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="text-foreground-800">年度盘点</span>
        </nav>

        <div className="mt-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary-600">
              <i className="ri-award-line" aria-hidden="true"></i>
              Annual Review
            </p>
            <h1 className="text-3xl font-black text-foreground-950 md:text-5xl">年度盘点</h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600">
              一年一度的重头戏。我们把全年最值得记住的热点按领域与后劲重新排序，从年度瓜王到年度名场面，逐年盘点、逐期讲透。
            </p>
          </div>
          <Link
            to="/monthly"
            className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-background-100 px-5 py-2.5 text-sm font-medium text-primary-700 ring-1 ring-background-200 transition-colors hover:bg-primary-100"
          >
            看月度总结
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {cats.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCat(c)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                cat === c
                  ? "bg-primary-500 font-semibold text-background-50"
                  : "bg-background-100 text-foreground-700 ring-1 ring-background-200 hover:bg-secondary-100"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* 年度瓜王主打 */}
      <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6">
        <Link
          to={`/rank/${featured.id}`}
          className="group grid grid-cols-1 overflow-hidden rounded-lg bg-background-50 ring-1 ring-background-200/70 transition-all hover:ring-primary-300 lg:grid-cols-2"
        >
          <div className="relative aspect-[16/10] overflow-hidden lg:aspect-auto lg:min-h-[360px]">
            <img
              src={featured.cover}
              alt={`${featured.title}主图`}
              title={`${featured.title} · 年度瓜王`}
              className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
            <span className="absolute left-4 top-4 flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-foreground-950">
              <i className="ri-trophy-fill" aria-hidden="true"></i>
              年度瓜王
            </span>
          </div>
          <div className="flex flex-col justify-center p-6 md:p-9">
            <div className="flex items-center gap-2">
              <Tag label={featured.category} />
              <span className="text-xs text-foreground-500">{featured.month}发布</span>
            </div>
            <h2 className="mt-3 text-2xl font-black leading-9 text-foreground-950 group-hover:text-primary-700 md:text-3xl">
              {featured.title}
            </h2>
            <p className="mt-3 text-sm leading-7 text-foreground-600">{featured.excerpt}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {featured.standouts?.map((s) => (
                <span key={s} className="whitespace-nowrap rounded-md bg-secondary-100 px-3 py-1 text-xs text-secondary-700">
                  <i className="ri-check-double-line mr-1" aria-hidden="true"></i>
                  {s}
                </span>
              ))}
            </div>
            <span className="mt-6 inline-flex items-center gap-1 whitespace-nowrap text-sm font-semibold text-primary-600">
              查看完整盘点
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" aria-hidden="true"></i>
            </span>
          </div>
        </Link>
      </div>

      {/* 其余排名列表 */}
      <div className="mx-auto w-full max-w-7xl px-4 pb-16 md:px-6">
        {list.length > 0 ? (
          <div className="flex flex-col gap-4">
            {list.map((a) => (
              <Link
                key={a.id}
                to={`/rank/${a.id}`}
                className="group grid grid-cols-1 gap-4 overflow-hidden rounded-lg bg-background-100/60 p-3 ring-1 ring-background-200/60 transition-all hover:ring-primary-300 sm:grid-cols-[120px_1fr] md:grid-cols-[180px_1fr]"
              >
                <div className="relative aspect-[3/2] overflow-hidden rounded-md sm:aspect-auto">
                  <img
                    src={a.cover}
                    alt={a.title}
                    className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute bottom-2 left-2 rounded-md bg-foreground-950/75 px-2 py-0.5 text-xs font-bold text-accent-300">
                    NO.{a.rank}
                  </span>
                </div>
                <div className="flex flex-col justify-center gap-2 py-1 sm:py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Tag label={a.category} />
                    <span className="flex items-center gap-1 text-xs text-primary-600">
                      <i className="ri-fire-line" aria-hidden="true"></i>
                      {a.heat}
                    </span>
                  </div>
                  <h3 className="line-clamp-1 text-base font-bold text-foreground-950 group-hover:text-primary-700 md:text-lg">
                    {a.title}
                  </h3>
                  <p className="line-clamp-2 text-sm text-foreground-600">{a.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="py-16 text-center text-foreground-600">该栏目暂无更多年度盘点，去看看其他栏目吧。</p>
        )}
      </div>
    </Layout>
  );
}